import { compose } from 'redux';
import moment from 'moment';

import CONST from '@/domain/documents/constants';
import isArray from 'lodash/isArray';

const DICT = /* :: Object.freeze( */ {
  financial: '_T_FIN',
  general: '_T_GEN',
  new: '_S_NEW',
  accepted: '_S_ACCEPTED',
  rejected: '_S_REJECTED',
  paid: CONST.tags.paid, // not sure if it is used, can be removed, need check
  unpaid: ['NOT', CONST.tags.paid], // not sure if it is used, can be removed, need check
  read: '_S_READ',
  approved: '_S_APPROVAL_COMPLETE',
  pending_approval: '_S_APPROVAL_PENDING',
}; /* :: ) */

function escapeUserInput(input) {
  return Array.isArray(input)
    ? input
        // escape user input for Lucene
        // @see https://lucene.apache.org/core/2_9_4/queryparsersyntax.html
        // eslint-disable-next-line no-useless-escape
        .map((tag) => tag.replace(/([\!\*\+\-\&\|\(\)\[\]\{\}\^\~\?\:\"\\\/])/g, '\\$1'))
    : input;
}

// eslint-disable-next-line max-len
function or(arr, wrapWithParenthes = false) {
  const res = arr
    .filter((f) => typeof f !== 'undefined')
    .map((e) => (isArray(e) ? e.map((i) => i.trim()) : e.trim()))
    .map((e) => {
      const [prefix, val] = isArray(e) ? e : [null, e];
      return prefix ? `${prefix} tags:${val}` : `tags:${val}`;
    })
    .map((e) => (wrapWithParenthes ? `(${e})` : e))
    .join(' OR ');
  return res;
}

function parseSearch(text) {
  if (typeof text === 'undefined') return [];
  return [
    text
      .map((e) => e.trim())
      .map((e) => (e.indexOf('#') === 0 ? `tags:"${e.substring(1)}"` : `${e}*`))
      .join(' AND '),
  ];
}

function concat(a, s) {
  return a.concat(`(${s})`);
}

function byDocumentType({ params, result, ...rest }) {
  const type =
    params.documentType && params.documentType !== 'approval'
      ? [DICT[params.documentType]]
      : [DICT.financial, DICT.general];
  return {
    ...rest,
    params,
    result: concat(result, or(type)),
  };
}

const extendWithClientTag = (isClient, user) => (tag) => {
  if (isClient && tag === DICT.pending_approval) return `${tag} AND approvals.next:'${user}'`;
  if (isClient && tag === DICT.approved) return `${tag} AND approvals.nodes.userid:'${user}'`;
  return tag;
};

function byTagName({ query, result, params }) {
  const isClient = ['user', 'confidential-user'].includes(params.user.role);
  const tags = or(
    (query.tags || []).map((e) => DICT[e]).map(extendWithClientTag(isClient, params.user.userId)),
    isClient, // will wrap conditions with ()
  );
  return {
    params,
    query,
    result: tags.length ? concat(result, tags) : result,
  };
}

function byText({ query, result, ...rest }) {
  const escapedText = escapeUserInput(query.text);
  const tags = parseSearch(escapedText);
  return {
    ...rest,
    query,
    result: tags.length ? concat(result, tags[0]) : result,
  };
}

function byQuery({ query, result, ...rest }) {
  const q = query.query;
  return {
    ...rest,
    query,
    result: q ? concat(result, q) : result,
  };
}

function byStag({ query, result, ...rest }) {
  const escapedText = escapeUserInput(query.stag);
  const tags = parseSearch(escapedText);
  return {
    ...rest,
    query,
    result: tags.length ? concat(result, tags[0]) : result,
  };
}

function byLinkedId({ query, result, ...rest }) {
  const { linkedId } = query;

  return {
    ...rest,
    query,
    result: linkedId ? concat(result, `linkid:${linkedId}`) : result,
  };
}

function isConfidential({ params, result, ...rest }) {
  const ct = '(tags:_S_CONFIDENTIAL)';
  return {
    ...rest,
    ...params,
    result: params.companyType === 'confidential' ? result.concat(ct) : result.concat(`NOT ${ct}`),
  };
}

const byDate =
  (inputKey, outputKey) =>
  ({ query, result, ...rest }) => {
    const dates = (query[inputKey] || [null]).map((e) => moment(e));
    const u = (d, idx) => {
      if (idx === dates.length - 1) {
        return d.set('hour', 23).set('minute', 59).set('second', 59).toISOString();
      }
      return d.toISOString();
    };
    return {
      ...rest,
      query,
      result: dates.some((e) => !e.isValid()) ? result : result.concat(`(${outputKey}:[${dates.map(u).join(' TO ')}])`),
    };
  };

function concatination({ result }) {
  return result.join(' AND ');
}

export function makeSearchQuery({ query, params }) {
  return compose(
    concatination,
    byLinkedId,
    isConfidential,
    byDate('date', 'created'),
    byDate('invoice_date', 'jeRepr.document_date'),
    byDate('payment_date', 'jeRepr.payment_date'),
    byStag,
    byText,
    byTagName,
    byDocumentType,
    byQuery,
  )({ query, params, result: [] });
}
